import React from "react";
import { useEval } from "../../hooks/fetch_api_hook/useEval";
import CompForEval from "./CompForEval";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2 } from 'lucide-react';
import { useClasseContext } from "../../context/ClasseContext";
import { renderTextWithLineBreaks } from "../../utils/DeleteBr";
import { formatLettresDate } from "../../utils/ClasseEvaluations";
import ExportNotes from "./ExportNotes";
import { useRef } from "react";
import { Lock, Unlock, Users, ChevronDown, ChevronRight } from 'lucide-react';
import { TruncatedTextWithTooltip } from "../general/TruncatedTextWithTooltip";
import PastilleNiveau, { horsNiveau, niveauDeLaClasse } from "../CollerNotes/PastilleNiveau";
import useUpdateGroupe from "../../hooks/update_api_hook/useUpdateGroupe";
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { url } from "../../utils/confserveur";
import Loading from "../general/Loading";
import useConfirm from "../../hooks/ui/useConfirm";
// react-router 6.18 n'expose que la version unstable_ du bloqueur (stabilisée
// en `useBlocker` à partir de 6.19), comme dans ProfesseurManagement : si un
// upgrade la renomme, la compilation casse ici, c'est voulu.
import { unstable_useBlocker as useBlockerCompat } from "react-router-dom";

// Échelle de maîtrise (scores 1→6) — partagée pastilles / légende-filtre /
// synthèse. Le CODE porte l'information (lisible daltoniens), la couleur la
// renforce ; PM et MF ont un texte foncé (fonds clairs).
const NIVEAUX = [
  { v: 6, code: 'TBM', label: 'Très bonne maîtrise', bg: 'bg-green-800', dot: '#15803d', texte: 'text-white' },
  { v: 5, code: 'MS', label: 'Maîtrise satisfaisante', bg: 'bg-green-500', dot: '#22c55e', texte: 'text-white' },
  { v: 4, code: 'PM', label: 'Presque maîtrisé', bg: 'bg-teal-200', dot: '#99f6e4', texte: 'text-teal-900' },
  { v: 3, code: 'MF', label: 'Maîtrise fragile', bg: 'bg-yellow-500', dot: '#eab308', texte: 'text-yellow-950' },
  { v: 2, code: 'DM', label: 'Début de maîtrise', bg: 'bg-orange-500', dot: '#f97316', texte: 'text-white' },
  { v: 1, code: 'MI', label: 'Maîtrise insuffisante', bg: 'bg-red-500', dot: '#ef4444', texte: 'text-white' },
];
const NIVEAU_PAR_SCORE = Object.fromEntries(NIVEAUX.map((n) => [n.v, n]));

/**
 * Texte d'un attendu en tête de colonne, précédé de « 6e » quand l'attendu
 * vient d'un autre niveau que la classe (2026-09-29 : le back envoie
 * `attenduNiveau` dans chaque ligne de résultat d'un attendu ; sans la clé,
 * vieille réponse en cache, rien ne change).
 */
const TexteAttendu = ({ header, niveauClasse }) => {
  const texte = (
    <TruncatedTextWithTooltip
      text={header.attendu}
      renderFunction={renderTextWithLineBreaks}
      maxLength={100}
      className=""
    />
  );
  if (!horsNiveau(header, niveauClasse)) return texte;
  return (
    <div className="flex items-baseline justify-center gap-1.5">
      <PastilleNiveau attendu={header} niveauClasse={niveauClasse} />
      {texte}
    </div>
  );
};

/** Score (0 si non noté) d'un élève pour une colonne (attendu/SF + compétence). */
const scorePourColonne = (eleve, header, competenceId) => {
  const r = (eleve.resultats || []).find((res) =>
    (((header.savoirFaireId && res.savoirFaireId === header.savoirFaireId) ||
      (header.attenduId && res.attenduId === header.attenduId)) &&
      res.competenceId === competenceId));
  return r && typeof r.scores === 'number' ? r.scores : 0;
};

/** Pastille de score : code + couleur (remplace le cercle muet). */
const PastilleScore = ({ score, dimmed }) => {
  const niveau = NIVEAU_PAR_SCORE[score];
  if (!niveau) {
    return (
      <span className={`inline-flex items-center justify-center min-w-[38px] px-2 py-0.5 rounded-full text-[10.5px] font-extrabold bg-gray-100 text-gray-400 border border-gray-200 transition-opacity ${dimmed ? 'opacity-10' : ''}`}>
        —
      </span>
    );
  }
  return (
    <span
      title={niveau.label}
      className={`inline-flex items-center justify-center min-w-[38px] px-2 py-0.5 rounded-full text-[10.5px] font-extrabold tracking-wide transition-opacity ${niveau.bg} ${niveau.texte} ${dimmed ? 'opacity-10' : ''}`}
    >
      {niveau.code}
    </span>
  );
};

// `embedded` : monté comme onglet (ex. segment "Suivi" de la page Résultats de
// session) → on masque le bouton RETOUR (la navigation se fait par onglets).
// `lectureSeule` : verrou imposé par le parent, indépendant du statut de
// l'éval (ex. séance en ligne pas encore close : la clôture note les copies
// et écraserait une correction faite à la main). Pastilles visibles, pas de
// saisie, pas de bouton Verrouiller/Déverrouiller ; `motifLectureSeule` est
// affiché en bandeau, `onActualiser` (facultatif) y ajoute un bouton pour
// relire le statut sans recharger la page.
function EvalDetail({selectedEval, setSelectedEvalId, selectedEvalId, embedded = false, lectureSeule = false, motifLectureSeule = '', onActualiser}) {
  const {classeNom, groupNom} = useClasseContext();

  const [modalShow, setModalShow] = useState(false);
  const [reloadEval, setReloadEval] = useState(false);
  const [storedResultatEval, setStoredResultatEval] = useState(null);

  const [statut, setStatut] = useState(selectedEval.statut);
  // Grille figée : verrou du parent OU éval terminée (bouton Verrouiller).
  const grilleVerrouillee = lectureSeule || statut === 'terminee';
  const {data: resultatEval, isLoading: isLoadingResultatEval, isError} = useEval(selectedEvalId+'/', reloadEval);
  
  const [{isLoading: isLoadingPost}, doPost] = usePostApi(url + "exercices_savoir_faire/mass_update_scores/");

  const [savoirFaireHeaders, setSavoirFaireHeaders] = useState([]);

  const [pendingUpdates, setPendingUpdates] = useState(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [selectedEleveId, setSelectedEleveId] = useState('');
  const [selectedEsfId, setSelectedEsfId] = useState('');
  const [selectedCellId, setSelectedCellId] = useState(null);

  // Redesign 06/2026 — affichage seul, ne touche pas aux données :
  // légende-filtre (met un niveau en évidence) + tri des lignes par compétence.
  const [highlightNiveau, setHighlightNiveau] = useState(null);
  // dir : 1 = fragiles d'abord, -1 = solides d'abord, 0 = ordre d'origine
  const [tri, setTri] = useState({ attenduId: null, savoirFaireId: null, competenceId: null, dir: 0 });


  const containerRef = useRef(null);
  const { updateGroupe } = useUpdateGroupe();

  // Responsive : table (scroll X, colonne élève sticky) à ≥ 1024 px ; en
  // dessous, vue CARTES accordéon — reconstruite sur les MÊMES données que
  // la table (elevesAffiches trié, PastilleScore, légende-filtre), là où
  // l'ancienne version divergeait (source non triée, pastilles muettes).
  const [estEtroit, setEstEtroit] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  ));
  const [expandedStudents, setExpandedStudents] = useState(() => new Set());
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e) => setEstEtroit(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const scrollToSelectedElement = useCallback(() => {
    const container = containerRef.current;
    const selectedElement = document.getElementById(selectedCellId);

    if (container && selectedElement) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();
      const offset = 20;

      // thead et tfoot sont STICKY : ils masquent une bande en haut et en bas
      // du conteneur. La zone réellement visible est entre les deux — sinon le
      // saut de ligne atterrit caché derrière la synthèse de classe.
      const theadH = container.querySelector('thead')?.getBoundingClientRect().height || 0;
      const tfootH = container.querySelector('tfoot')?.getBoundingClientRect().height || 0;
      const visibleTop = containerRect.top + theadH;
      const visibleBottom = containerRect.bottom - tfootH;

      if (elementRect.bottom > visibleBottom) {
        container.scrollTop += elementRect.bottom - visibleBottom + offset;
      } else if (elementRect.top < visibleTop) {
        container.scrollTop -= visibleTop - elementRect.top + offset;
      }
    }
  }, [selectedCellId]);

  const updateStoredResultatLocal = (eleveId, esfId, newScore) => {
    setStoredResultatEval(prev => {
      const updatedEleveResultats = prev.eleves_resultats.map(eleve => {
        if (eleve.id === eleveId) {
          const updatedResultats = eleve.resultats.map(resultat => {
            if (resultat.esfId === esfId) {
              return { ...resultat, scores: newScore !== 0 ? newScore : null };
            }
            return resultat;
          });
          return { ...eleve, resultats: updatedResultats };
        }
        return eleve;
      });
      return { ...prev, eleves_resultats: updatedEleveResultats };
    });
  };

  const handleScoreChange = (eleveId, esfId, newScore) => {
    setPendingUpdates(prev => {
      const newMap = new Map(prev);
      newMap.set(`${eleveId}-${esfId}`, { eleveId, esfId, score: newScore });
      return newMap;
    });
    setHasUnsavedChanges(true);
    updateStoredResultatLocal(eleveId, esfId, newScore);
  };

  const handleCancelChanges = () => {
    setPendingUpdates(new Map());
    setHasUnsavedChanges(false);
    setStoredResultatEval(null);
    setReloadEval(!reloadEval);
  };

  useEffect(() => {
    if (selectedCellId) {
      setTimeout(() => {
        scrollToSelectedElement();
      }, 100);
    }
  }, [selectedCellId, storedResultatEval, scrollToSelectedElement]);

  // Vue cartes : la navigation (saut de cellule) ouvre l'accordéon de
  // l'élève visé et l'amène à l'écran.
  useEffect(() => {
    if (!estEtroit || !selectedCellId) return;
    const match = selectedCellId.match(/eleve-(\d+)-esf-/);
    if (!match) return;
    const eleveId = parseInt(match[1], 10);
    setExpandedStudents((prev) => {
      if (prev.has(eleveId)) return prev;
      const next = new Set(prev);
      next.add(eleveId);
      return next;
    });
    setTimeout(() => {
      document.getElementById(`student-card-${eleveId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  }, [estEtroit, selectedCellId]);

  const moveHeaderUp = (index) => {
    setSavoirFaireHeaders((prevHeaders) => {
      const newHeaders = [...prevHeaders];
      const temp = newHeaders[index - 1];
      newHeaders[index - 1] = newHeaders[index];
      newHeaders[index] = temp;
      return newHeaders;
    });
  };
  
  const moveHeaderDown = (index) => {
    setSavoirFaireHeaders((prevHeaders) => {
      const newHeaders = [...prevHeaders];
      const temp = newHeaders[index + 1];
      newHeaders[index + 1] = newHeaders[index];
      newHeaders[index] = temp;
      return newHeaders;
    });
  };

  const moveCompetenceUp = (headerIndex, compIndex) => {
    setSavoirFaireHeaders((prevHeaders) => {
      const newHeaders = [...prevHeaders];
      const selectedHeader = newHeaders[headerIndex];
      const newCompetences = [...selectedHeader.competences];
      const temp = newCompetences[compIndex - 1];
      newCompetences[compIndex - 1] = newCompetences[compIndex];
      newCompetences[compIndex] = temp;
      selectedHeader.competences = newCompetences;
      return newHeaders;
    });
  };
  
  const moveCompetenceDown = (headerIndex, compIndex) => {
    setSavoirFaireHeaders((prevHeaders) => {
      const newHeaders = [...prevHeaders];
      const selectedHeader = newHeaders[headerIndex];
      const newCompetences = [...selectedHeader.competences];
      const temp = newCompetences[compIndex + 1];
      newCompetences[compIndex + 1] = newCompetences[compIndex];
      newCompetences[compIndex] = temp;
      selectedHeader.competences = newCompetences;
      return newHeaders;
    });
  };

  useEffect(() => {
    if (resultatEval && resultatEval.eleves_resultats && !storedResultatEval) {
      setStoredResultatEval(resultatEval);
    }
  }, [resultatEval, storedResultatEval]);
  useEffect(() => {
    if (resultatEval && resultatEval.eleves_resultats) {
      const savoirFaireMap = new Map();
      const attenduMap = new Map();

      resultatEval.eleves_resultats.forEach(eleve => {
        eleve.resultats.forEach(res => {
          if (res.savoirFaireId && !savoirFaireMap.has(res.savoirFaireId)) {
            savoirFaireMap.set(res.savoirFaireId, {
              savoirFaire: res.savoirTitre,
              savoirFaireId: res.savoirFaireId,
              competences: new Map()
            });
          }
          if (res.savoirFaireId) {
            savoirFaireMap.get(res.savoirFaireId).competences.set(res.competenceId, res.competenceNom);
          }

          if (res.attenduId && !attenduMap.has(res.attenduId)) {
            attenduMap.set(res.attenduId, {
              attendu: res.attenduTitre,
              attenduId: res.attenduId,
              niveau: res.attenduNiveau,
              competences: new Map()
            });
          }
          if (res.attenduId) {
            attenduMap.get(res.attenduId).competences.set(res.competenceId, res.competenceNom);
          }
        });
      });

      const attenduHeaders = Array.from(attenduMap.values()).map(at => ({
        attendu: at.attendu,
        attenduId: at.attenduId,
        niveau: at.niveau,
        competences: Array.from(at.competences.entries()).map(([compId, compNom]) => ({
          competenceId: compId,
          competenceNom: compNom,
        })),
      }));

      const savoirFaireLocalHeaders = Array.from(savoirFaireMap.values()).map(sf => ({
        savoirFaire: sf.savoirFaire,
        savoirFaireId: sf.savoirFaireId,
        competences: Array.from(sf.competences.entries()).map(([compId, compNom]) => ({
          competenceId: compId,
          competenceNom: compNom,
        })),
      }));

      const headers = [...attenduHeaders, ...savoirFaireLocalHeaders];
      setSavoirFaireHeaders(headers);
    }
  }, [resultatEval]);

  // ── Gardes de sortie (2026-09-29). Des notes saisies mais pas sauvegardées
  // se perdaient sans un mot dès qu'on cliquait sur le menu, sur le fil
  // d'Ariane (« Classe 6A ») ou sur le retour du navigateur : ce sont des
  // changements de page du routeur, que beforeunload ne voit pas (l'ancien
  // garde popstate arrivait après coup). Deux couvertures, comme la gestion
  // des professeurs : le bloqueur du routeur retient TOUTE navigation et pose
  // une seule question ; fermer ou recharger l'onglet garde le dialogue natif.
  const { confirm, ConfirmDialog } = useConfirm();
  const nbNonSauvegardeesRef = useRef(0);
  nbNonSauvegardeesRef.current = pendingUpdates.size;
  // Le bouton RETOUR pose la question lui-même (il ne navigue pas toujours :
  // page des devoirs communs), puis laisse passer sa propre navigation.
  const sortieConfirmeeRef = useRef(false);
  const questionSortie = useCallback(() => confirm({
    title: 'Quitter sans sauvegarder ?',
    message: `${nbNonSauvegardeesRef.current} note(s) non sauvegardée(s) seront perdues si vous quittez sans cliquer sur « Sauvegarder ».`,
    confirmLabel: 'Quitter sans sauvegarder',
    variant: 'danger',
  }), [confirm]);
  const retenirSortie = useCallback(
    () => hasUnsavedChanges && !sortieConfirmeeRef.current,
    [hasUnsavedChanges],
  );
  const blocker = useBlockerCompat(retenirSortie);
  useEffect(() => {
    if (!blocker || blocker.state !== 'blocked') return;
    (async () => {
      if (await questionSortie()) blocker.proceed();
      else blocker.reset();
    })();
  }, [blocker, questionSortie]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const sortResultsBySavoirFaireHeaders = (results, savoirFaireHeaders) => {
    return results.sort((a, b) => {
      const indexA = savoirFaireHeaders.findIndex(header => 
        (header.savoirFaireId && header.savoirFaireId === a.savoirFaireId) ||
        (header.attenduId && header.attenduId === a.attenduId)
      );
      const indexB = savoirFaireHeaders.findIndex(header => 
        (header.savoirFaireId && header.savoirFaireId === b.savoirFaireId) ||
        (header.attenduId && header.attenduId === b.attenduId)
      );

      if (indexA === indexB) {
        const currentHeader = savoirFaireHeaders[indexA];
        const compIndexA = currentHeader.competences.findIndex(comp => comp.competenceId === a.competenceId);
        const compIndexB = currentHeader.competences.findIndex(comp => comp.competenceId === b.competenceId);
        return compIndexA - compIndexB;
      }

      return indexA - indexB;
    });
  };

  useEffect(() => {
    if (storedResultatEval && storedResultatEval.eleves_resultats) {
      const sortedResults = storedResultatEval.eleves_resultats.map(eleve => ({
        ...eleve,
        resultats: sortResultsBySavoirFaireHeaders([...eleve.resultats], savoirFaireHeaders),
      }));

      const isDifferent = JSON.stringify(sortedResults) !== JSON.stringify(storedResultatEval.eleves_resultats);

      if (isDifferent) {
        setStoredResultatEval({
          ...storedResultatEval,
          eleves_resultats: sortedResults,
        });
      }
    }
  }, [savoirFaireHeaders, storedResultatEval]);

  // ── Lignes affichées : ordre d'origine, ou triées par la colonne choisie.
  // Dérivé au render (useMemo) — storedResultatEval n'est JAMAIS muté, et la
  // navigation "cellule suivante" suit ce même ordre affiché.
  const elevesAffiches = useMemo(() => {
    const eleves = storedResultatEval?.eleves_resultats || [];
    if (tri.dir === 0 || (!tri.attenduId && !tri.savoirFaireId)) return eleves;
    const nomDe = (e) => (e.eleve_gar ? `${e.eleve_gar_nom} ${e.eleve_gar_prenom}` : `${e.nom} ${e.prenom}`);
    const header = { attenduId: tri.attenduId, savoirFaireId: tri.savoirFaireId };
    return [...eleves].sort((a, b) =>
      (scorePourColonne(a, header, tri.competenceId) - scorePourColonne(b, header, tri.competenceId)) * tri.dir
      || nomDe(a).localeCompare(nomDe(b)));
  }, [storedResultatEval, tri]);

  // Synthèse de classe par colonne (compteurs par niveau, part ≥ PM, fragiles)
  const syntheseColonnes = useMemo(() => {
    const eleves = storedResultatEval?.eleves_resultats || [];
    return savoirFaireHeaders.map((header) =>
      header.competences.map((comp) => {
        const compte = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
        eleves.forEach((eleve) => { compte[scorePourColonne(eleve, header, comp.competenceId)] += 1; });
        const total = eleves.length;
        const fragiles = compte[1] + compte[2] + compte[3];
        const nonNotes = compte[0];
        return { compte, total, fragiles, nonNotes, solides: total - fragiles - nonNotes };
      })
    );
  }, [storedResultatEval, savoirFaireHeaders]);

  /** La colonne (header, comp) est-elle celle du tri actif ? */
  const estColonneTriee = (header, comp) =>
    tri.dir !== 0 &&
    tri.competenceId === comp.competenceId &&
    tri.attenduId === (header.attenduId || null) &&
    tri.savoirFaireId === (header.savoirFaireId || null);

  // Cycle de tri au clic sur une compétence : fragiles → solides → ordre initial
  const handleTriCompetence = (header, comp) => {
    setTri((prev) => {
      const memeColonne = prev.competenceId === comp.competenceId
        && prev.attenduId === (header.attenduId || null)
        && prev.savoirFaireId === (header.savoirFaireId || null);
      if (!memeColonne) {
        return { attenduId: header.attenduId || null, savoirFaireId: header.savoirFaireId || null, competenceId: comp.competenceId, dir: 1 };
      }
      if (prev.dir === 1) return { ...prev, dir: -1 };
      return { attenduId: null, savoirFaireId: null, competenceId: null, dir: 0 };
    });
  };

  const handleCellClick = useCallback((eleveid, esfid, e1, e3, e4) => {
    if (lectureSeule) return;
    setModalShow(true);
    setSelectedEleveId(eleveid);
    setSelectedEsfId(esfid);
    setSelectedCellId(`eleve-${eleveid}-esf-${esfid}`);
  }, [lectureSeule]);

    // Navigation après saisie : suit l'ORDRE AFFICHÉ (elevesAffiches), pas
    // l'ordre brut — sinon, tri actif, le "suivant" sauterait visuellement.
    const navigateToNext = useCallback(() => {
    if (elevesAffiches.length === 0) return;

    const currentEleveIndex = elevesAffiches.findIndex(e => e.id === selectedEleveId);
    if (currentEleveIndex === -1) return;
    const currentResultIndex = elevesAffiches[currentEleveIndex].resultats.findIndex(r => r.esfId === selectedEsfId);

    let nextEleveIndex = currentEleveIndex;
    let nextResultIndex = currentResultIndex + 1;

    if (nextResultIndex >= elevesAffiches[currentEleveIndex].resultats.length) {
      if (currentEleveIndex === elevesAffiches.length - 1) {
        return;
      } else {
        nextResultIndex = 0;
        nextEleveIndex = currentEleveIndex + 1;
      }
    }

    const nextEleve = elevesAffiches[nextEleveIndex];
    const nextResult = nextEleve.resultats[nextResultIndex];

    setSelectedEleveId(nextEleve.id);
    setSelectedEsfId(nextResult.esfId);
    setSelectedCellId(`eleve-${nextEleve.id}-esf-${nextResult.esfId}`);
  }, [elevesAffiches, selectedEleveId, selectedEsfId]);

  
  const handleClose = useCallback((shouldReload) => {
    setModalShow(false);
    if (shouldReload === "reload") {
      navigateToNext();
    } 
  }, [navigateToNext]);

  const handleEditStatut = async (item) => {
    try {
      await updateGroupe(`${url}evaluations/${selectedEvalId}/`, { statut: item });
      alert("✅ Statut mis à jour avec succès !");
      setStatut(item);
    } catch (err) {
      console.error("Erreur lors de la mise à jour :", err);
      alert("❌ Erreur lors de la mise à jour du statut. Veuillez réessayer.");
    }
  };

  const handleRetourClick = async () => {
    if (hasUnsavedChanges) {
      if (!(await questionSortie())) return;
      // Sa navigation (?eval= vidé) ne doit pas reposer la question.
      sortieConfirmeeRef.current = true;
    }
    setSelectedEvalId(null);
  };

  const handleBatchSave = async () => {
    if (pendingUpdates.size === 0) return;
    
    setIsSaving(true);
    
    try {
      const updatesArray = Array.from(pendingUpdates.values()).map(update => ({
        esfId: update.esfId,
        score: update.score !== 0 ? update.score : null
      }));
      
      await doPost(updatesArray);
      
      setPendingUpdates(new Map());
      setHasUnsavedChanges(false);
      
      alert(`✅ ${updatesArray.length} modification(s) sauvegardée(s) !`);
      
    } catch (error) {
      console.error("❌ Erreur batch save:", error);
      alert("❌ Erreur lors de la sauvegarde. Veuillez réessayer.");
    } finally {
      setIsSaving(false);
    }
  };

  // Synthèse de classe par colonne : barre de distribution + « X/N ≥ presque
  // maîtrisé · Y fragiles ». Recalculée en direct (storedResultatEval est mis
  // à jour à chaque saisie, avant même la sauvegarde batch).
  const tableFoot = (
    <tfoot>
      <tr>
        <td className="sticky bottom-0 left-0 z-20 bg-white border-t-2 border-t-[#D5E6F3] border-r border-r-[#E8F3FB] px-4 py-2 text-left text-[11.5px] font-extrabold text-slate-400 whitespace-nowrap">
          Classe ({storedResultatEval?.eleves_resultats?.length || 0})
        </td>
        {groupNom && <td className="sticky bottom-0 bg-white border-t-2 border-[#D5E6F3]"></td>}
        {groupNom && <td className="sticky bottom-0 bg-white border-t-2 border-[#D5E6F3]"></td>}
        {savoirFaireHeaders.map((header, headerIndex) =>
          header.competences.map((comp, compIndex) => {
            const s = syntheseColonnes[headerIndex]?.[compIndex];
            if (!s || s.total === 0) {
              return <td key={comp.competenceId} className="sticky bottom-0 bg-white border-t-2 border-[#D5E6F3]"></td>;
            }
            return (
              <td key={comp.competenceId} className="sticky bottom-0 bg-white border-t-2 border-[#D5E6F3] px-3 pt-2 pb-2.5">
                <div className="flex h-[9px] w-4/5 mx-auto mb-1 rounded-full overflow-hidden shadow-[inset_0_0_0_1px_rgba(19,40,63,0.08)]">
                  {NIVEAUX.map((n) => (s.compte[n.v] > 0 ? (
                    <span
                      key={n.v}
                      className="h-full"
                      style={{ width: `${(s.compte[n.v] / s.total) * 100}%`, background: n.dot }}
                      title={`${n.label} : ${s.compte[n.v]}`}
                    />
                  ) : null))}
                  {s.nonNotes > 0 ? (
                    <span className="h-full bg-gray-200" style={{ width: `${(s.nonNotes / s.total) * 100}%` }} title={`Non noté : ${s.nonNotes}`} />
                  ) : null}
                </div>
                {/* Deux stats EMPILÉES : le nowrap en ligne imposait
                    ~160 px de plancher par colonne (scroll X gonflé). */}
                <div className="text-[10.5px] font-extrabold text-slate-400">
                  <span className="block whitespace-nowrap"><b className="text-slate-800">{s.solides}</b>/{s.total} ≥ presque maîtrisé</span>
                  {s.fragiles > 0 ? <span className="block whitespace-nowrap text-orange-700">{s.fragiles} fragile{s.fragiles > 1 ? 's' : ''}</span> : null}
                </div>
              </td>
            );
          })
        )}
      </tr>
    </tfoot>
  );

  // ─── Vue cartes (< 1024 px) : un accordéon par élève, mêmes données que
  // la table (tri, sélection, légende-filtre) et mêmes ids de cellules
  // (la navigation clavier et le scroll automatique continuent de marcher).
  const toggleStudentExpansion = (studentId) => {
    setExpandedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  const renderCartesView = () => (
    <div className={`${grilleVerrouillee ? 'pointer-events-none opacity-50 select-none' : ''} p-3 space-y-2.5`}>
      {elevesAffiches.map((eleve) => {
        const ouvert = expandedStudents.has(eleve.id);
        const nomComplet = eleve.eleve_gar
          ? `${eleve.eleve_gar_nom} ${eleve.eleve_gar_prenom}`
          : `${eleve.nom} ${eleve.prenom}`;
        return (
          <div key={eleve.id} id={`student-card-${eleve.id}`} className="bg-white border border-[#D5E6F3] rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => toggleStudentExpansion(eleve.id)}
              className="w-full px-4 py-3 flex items-center gap-2 text-left bg-[#F7FBFE] hover:bg-[#EFF7FC] transition-colors"
            >
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 text-[13px] font-bold text-slate-800">
                  <span className="truncate">{nomComplet}</span>
                  {eleve.eleve_gar ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 flex-shrink-0">GAR</span>
                  ) : null}
                </span>
                {groupNom ? (
                  <span className="block text-[11px] font-semibold text-slate-400 mt-0.5">
                    Classe {eleve.classe_nom} · Groupe {eleve.groupe_nom}
                  </span>
                ) : null}
              </span>
              {ouvert
                ? <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
                : <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />}
            </button>
            {ouvert ? (
              <div className="px-4 py-3 space-y-3 border-t border-[#EDF5FA]">
                {savoirFaireHeaders.map((savoir, index) => (
                  <div key={index}>
                    <p className="text-[12px] font-extrabold text-slate-700 leading-snug mb-1.5">
                      {renderTextWithLineBreaks(savoir.attendu || savoir.savoirFaire)}
                    </p>
                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-1.5">
                      {savoir.competences.map((comp, compIndex) => {
                        const matchingResult = eleve.resultats.find((result) => (
                          ((savoir.savoirFaireId && result.savoirFaireId === savoir.savoirFaireId) ||
                            (savoir.attenduId && result.attenduId === savoir.attenduId)) &&
                          result.competenceId === comp.competenceId
                        ));
                        const estSelectionnee = matchingResult && selectedCellId === `eleve-${eleve.id}-esf-${matchingResult.esfId}`;
                        const dimmed = highlightNiveau !== null && (!matchingResult || matchingResult.scores !== highlightNiveau);
                        return (
                          <button
                            type="button"
                            key={matchingResult ? matchingResult.esfId : `no-match-${index}-${compIndex}`}
                            id={matchingResult ? `eleve-${eleve.id}-esf-${matchingResult.esfId}` : undefined}
                            onClick={() => matchingResult && handleCellClick(
                              eleve.id,
                              matchingResult.esfId,
                              matchingResult.savoirTitre ? matchingResult.savoirTitre : matchingResult.attenduTitre,
                              matchingResult.competenceNom,
                              matchingResult.scores
                            )}
                            className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border transition-colors ${
                              estSelectionnee
                                ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-300'
                                : 'border-slate-100 bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            <span className="text-[11.5px] font-bold text-slate-500 text-left">{comp.competenceNom}</span>
                            <PastilleScore score={matchingResult ? matchingResult.scores : null} dimmed={dimmed} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  // Desktop View
  const renderDesktopView = () => (
    <table className={`${grilleVerrouillee ? 'pointer-events-none opacity-50 select-none' : ''} w-full text-center border-separate border-spacing-0`}>
      {tableHeaders}
      <tbody>{tableBody}</tbody>
      {tableFoot}
    </table>
  );

  // Niveau de la classe pour la pastille des attendus d'un autre niveau : le
  // nom de la classe (ou du groupe), à défaut la classe d'origine d'un élève
  // (groupe dont le nom ne commence pas par son niveau).
  const niveauClasse = niveauDeLaClasse(classeNom || groupNom, storedResultatEval?.eleves_resultats);

  // En-têtes redesign maquette 06/2026 : NEUTRES (fond blanc, la couleur est
  // réservée aux pastilles de données) ; barre d'accent cyan = attendu,
  // émeraude = savoir-faire (l'ancienne distinction teal/vert, en discret).
  // Les flèches ←/→ gardent leurs handlers de réordonnancement à l'identique.
  const tableHeaders = (
    <thead className="sticky top-0 z-20">
      <tr>
        <th rowSpan="2" className="sticky left-0 z-30 bg-white border-r border-r-[#E8F3FB] border-b-2 border-b-[#D5E6F3] px-4 text-left align-bottom pb-2 max-w-[190px]">
          <div className="text-xs font-extrabold text-slate-500">Nom de l'élève</div>
          <div className="text-[11px] font-bold text-slate-400 mt-0.5">{tri.dir === 0 ? 'Ordre de la classe' : 'Trié par compétence'}</div>
        </th>
        {groupNom && (
          <>
            <th className="bg-white border-b border-[#E8F3FB] px-3 pt-2.5 text-xs font-extrabold text-slate-500">Classe d'origine</th>
            <th className="bg-white border-b border-[#E8F3FB] px-3 pt-2.5 text-xs font-extrabold text-slate-500">Groupe actuel</th>
          </>
        )}
        {savoirFaireHeaders.map((header, index) => (
          <th
            key={index}
            colSpan={header.competences.length}
            className="bg-white border-b border-[#E8F3FB] px-3 pt-2.5 pb-1 text-[12px] font-extrabold text-slate-800 leading-snug align-top"
          >
            {/* Largeur de colonne plafonnée : sans le max-w, un intitulé de
                100 caractères fixait la largeur de toute la colonne. */}
            <div className="max-w-[260px] mx-auto">
            {header.attendu && (
              <TexteAttendu header={header} niveauClasse={niveauClasse} />
            )}
            {header.savoirFaire && (
              <TruncatedTextWithTooltip
                text={header.savoirFaire}
                renderFunction={renderTextWithLineBreaks}
                maxLength={100}
                className=""
              />
            )}
            </div>
            <span className={`block w-9 h-[3px] rounded-full mx-auto mt-1.5 ${header.savoirFaire ? 'bg-emerald-400' : 'bg-cyan-400'}`} />
            {/* Flèches bien visibles (pastilles teintées charte) — mêmes handlers */}
            <div className="flex items-center justify-center gap-1.5 mt-1.5">
              <button
                className={`w-7 h-6 rounded-lg text-sm font-extrabold flex items-center justify-center border transition-colors ${
                  index === 0
                    ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                    : 'bg-[#EDF6FC] text-blue-600 border-[#D5E6F3] hover:bg-[#DEEEFA] hover:border-cyan-400'
                }`}
                onClick={() => moveHeaderUp(index)}
                disabled={index === 0}
                title="Décaler à gauche"
              >
                ←
              </button>
              <button
                className={`w-7 h-6 rounded-lg text-sm font-extrabold flex items-center justify-center border transition-colors ${
                  index === savoirFaireHeaders.length - 1
                    ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                    : 'bg-[#EDF6FC] text-blue-600 border-[#D5E6F3] hover:bg-[#DEEEFA] hover:border-cyan-400'
                }`}
                onClick={() => moveHeaderDown(index)}
                disabled={index === savoirFaireHeaders.length - 1}
                title="Décaler à droite"
              >
                →
              </button>
            </div>
          </th>
        ))}
      </tr>
      <tr>
        {groupNom && <th className="bg-white border-b-2 border-[#D5E6F3]"></th>}
        {groupNom && <th className="bg-white border-b-2 border-[#D5E6F3]"></th>}
        {savoirFaireHeaders.map((header, headerIndex) =>
          header.competences.map((comp, compIndex) => (
            <th
              key={comp.competenceId}
              onClick={() => handleTriCompetence(header, comp)}
              className="bg-white border-b-2 border-[#D5E6F3] px-2.5 pt-1 pb-1.5 text-[12.5px] font-extrabold text-blue-600 cursor-pointer hover:bg-[#F2F8FC] select-none transition-colors"
              title="Cliquer pour trier les élèves sur cette compétence"
            >
              {comp.competenceNom}
              <span className="ml-1 text-[10px] font-bold opacity-60">
                {estColonneTriee(header, comp) ? (tri.dir === 1 ? '↑ fragiles' : '↓ solides') : '↕'}
              </span>
              {/* Flèches de réordonnancement — stopPropagation : un clic
                  flèche ne doit JAMAIS déclencher le tri */}
              <div className="flex items-center justify-center gap-1 mt-1">
                <button
                  className={`w-6 h-5 rounded-md text-xs font-extrabold flex items-center justify-center border transition-colors ${
                    compIndex === 0
                      ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                      : 'bg-[#EDF6FC] text-blue-600 border-[#D5E6F3] hover:bg-[#DEEEFA] hover:border-cyan-400'
                  }`}
                  onClick={(e) => { e.stopPropagation(); moveCompetenceUp(headerIndex, compIndex); }}
                  disabled={compIndex === 0}
                  title="Décaler à gauche"
                >
                  ←
                </button>
                <button
                  className={`w-6 h-5 rounded-md text-xs font-extrabold flex items-center justify-center border transition-colors ${
                    compIndex === header.competences.length - 1
                      ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                      : 'bg-[#EDF6FC] text-blue-600 border-[#D5E6F3] hover:bg-[#DEEEFA] hover:border-cyan-400'
                  }`}
                  onClick={(e) => { e.stopPropagation(); moveCompetenceDown(headerIndex, compIndex); }}
                  disabled={compIndex === header.competences.length - 1}
                  title="Décaler à droite"
                >
                  →
                </button>
              </div>
            </th>
          ))
        )}
      </tr>
    </thead>
  );

  // Corps redesign : lignes denses 42px, colonne noms sticky, pastilles à
  // code (PastilleScore), dimming par la légende-filtre. Ids de cellules et
  // handleCellClick STRICTEMENT inchangés (auto-scroll + modal en dépendent).
  const tableBody = (
    <>
      {elevesAffiches.map(eleve => (
        <tr className="group h-[42px] border-b border-[#EDF5FA] hover:bg-[#F2F8FC] transition-colors" key={eleve.id}>
          <td className="sticky left-0 z-10 bg-white group-hover:bg-[#F2F8FC] border-r border-[#E8F3FB] px-4 text-left text-[13px] font-bold text-slate-800 whitespace-nowrap max-w-[190px]">
            <span
              className="inline-block max-w-[130px] truncate align-middle"
              title={eleve.eleve_gar
                ? `${eleve.eleve_gar_nom} ${eleve.eleve_gar_prenom}`
                : `${eleve.nom} ${eleve.prenom}`}
            >
              {eleve.eleve_gar
                ? `${eleve.eleve_gar_nom} ${eleve.eleve_gar_prenom}`
                : `${eleve.nom} ${eleve.prenom}`
              }
            </span>
            {eleve.eleve_gar && (
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                GAR
              </span>
            )}
          </td>
          {groupNom && <td className="px-2 text-xs font-semibold text-slate-500">{eleve.classe_nom}</td>}
          {groupNom && <td className="px-2 text-xs font-semibold text-slate-500">{eleve.groupe_nom}</td>}
          {savoirFaireHeaders.map((savoir, index) => (
            savoir.competences.map((comp, compIndex) => {
              const matchingResult = eleve.resultats.find(result => {
                return (
                  ((savoir.savoirFaireId && (result.savoirFaireId === savoir.savoirFaireId)) ||
                   (savoir.attenduId && (result.attenduId === savoir.attenduId))) &&
                  result.competenceId === comp.competenceId
                );
              });
              const estSelectionnee = matchingResult && selectedCellId === `eleve-${eleve.id}-esf-${matchingResult.esfId}`;
              // Légende-filtre : tout ce qui n'est pas du niveau choisi s'estompe
              const dimmed = highlightNiveau !== null && (!matchingResult || matchingResult.scores !== highlightNiveau);
              return (
                <td
                  id={`eleve-${eleve.id}-esf-${matchingResult ? matchingResult.esfId : ''}`}
                  key={`${matchingResult ? matchingResult.esfId : `no-match-${index}-${compIndex}`}`}
                  onClick={() => matchingResult && handleCellClick(eleve.id, matchingResult.esfId, matchingResult.savoirTitre ? matchingResult.savoirTitre : matchingResult.attenduTitre, matchingResult.competenceNom, matchingResult.scores)}
                  className={`px-2 cursor-pointer ${estSelectionnee ? 'bg-blue-50 ring-2 ring-inset ring-blue-400' : ''}`}
                >
                  {(estSelectionnee && isLoadingResultatEval) ? (
                    <div className="w-full flex justify-center">
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    </div>
                  ) : (
                    <PastilleScore score={matchingResult ? matchingResult.scores : null} dimmed={dimmed} />
                  )}
                </td>
              );
            })
          ))}
        </tr>
      ))}
    </>
  );

  // {ConfirmDialog} monté aussi pendant un rechargement : une navigation
  // retenue à ce moment doit pouvoir afficher sa question.
  if (isError) {
    return <div>Une erreur s'est produite ou les données ne sont pas disponible.{ConfirmDialog}</div>;
  }
  if (!resultatEval || !resultatEval.eleves_resultats) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="w-12 h-12 text-blue-500 animate-spin" />{ConfirmDialog}</div>;
  }

  // Classe sans élèves : lu sur l'état AFFICHÉ (storedResultatEval, replié sur
  // la donnée fraîche) — tableau vide = état vide explicite, pas de grille
  const lignesEleves = (storedResultatEval || resultatEval)?.eleves_resultats;
  const aucunEleve = Array.isArray(lignesEleves) && lignesEleves.length === 0;

  return (
    <div className="w-full">
      {ConfirmDialog}
      {/* Bouton retour — masqué en mode onglet (embedded) */}
      {!embedded && (
        <div>
          <button
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all duration-200 hover:-translate-y-0.5 text-xs sm:text-sm"
            onClick={handleRetourClick}
          >
            ← RETOUR
          </button>
        </div>
      )}

      {/* Verrou du parent : le motif en bandeau, la grille reste lisible. */}
      {lectureSeule && motifLectureSeule ? (
        <div role="status" className="mt-3 sm:mt-6 flex items-center gap-3 flex-wrap bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-2xl px-4 py-3 text-sm font-bold">
          <Lock size={16} className="shrink-0" />
          <span className="min-w-0">{motifLectureSeule}</span>
          {onActualiser ? (
            <button
              type="button"
              onClick={onActualiser}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 text-[13px] font-extrabold transition-colors"
            >
              Actualiser
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Carte récap — maquette 06/2026 : titre + stats + actions sur une
          seule carte, naturellement responsive (flex-wrap remplace les deux
          variantes mobile/desktop). La barre de répartition globale est
          retirée : la synthèse PAR COLONNE du tableau la remplace. */}
      <div className="bg-white border-2 border-[#D5E6F3] rounded-2xl shadow-sm my-3 sm:my-6 px-4 sm:px-5 py-3.5 flex items-center gap-x-4 sm:gap-x-6 gap-y-3 flex-wrap">
        <div className="min-w-0 max-w-full">
          <div className="text-[11px] font-extrabold tracking-wide uppercase text-cyan-600">
            📄 Suivi de notes{(classeNom || groupNom) ? ` · ${classeNom || groupNom}` : ''}
          </div>
          <h2 className="text-lg font-extrabold text-slate-800 leading-tight">{selectedEval.titre}</h2>
        </div>
        <div className="text-center">
          <div className="text-base font-extrabold text-slate-800">{formatLettresDate(selectedEval.date)}</div>
          <div className="text-[11px] font-bold text-slate-400">Date</div>
        </div>
        {/* Absents / Moyenne : masqués en mode onglet (le résumé de l'éval
            n'est pas chargé ici ; le contexte session est déjà dans l'en-tête). */}
        {!embedded && (
          <>
            <div className="text-center">
              <div className="text-base font-extrabold text-slate-800">{selectedEval.nombre_eleves || 0}</div>
              <div className="text-[11px] font-bold text-slate-400">Absents</div>
            </div>
            <div className="text-center">
              <div className="text-base font-extrabold text-slate-800">
                {selectedEval.moyenne_scores
                  ? <>{parseFloat(selectedEval.moyenne_scores.toFixed(2))}<span className="text-xs font-bold text-slate-400">/20</span></>
                  : 'Pas de note'}
              </div>
              <div className="text-[11px] font-bold text-slate-400">Moyenne classe</div>
            </div>
          </>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* Export unifié (Notes converties / Par compétence / Les deux) ;
              le collage de notes, lui, vit dans « Coller mes notes
              existantes » côté création d'évaluation. */}
          <ExportNotes resultatEval={storedResultatEval} />
          {lectureSeule ? null : (
          <button
            onClick={() => handleEditStatut(statut === 'en_attente' ? 'terminee' : 'en_attente')}
            title={statut === 'en_attente' ? 'Verrouiller la saisie (terminer)' : 'Déverrouiller la saisie'}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-extrabold transition-colors ${
              statut === 'en_attente'
                ? 'bg-[#F2F8FC] text-blue-600 hover:bg-[#E8F3FB]'
                : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}
          >
            {statut === 'en_attente' ? <Lock size={14} /> : <Unlock size={14} />}
            {statut === 'en_attente' ? 'Verrouiller' : 'Déverrouiller'}
          </button>
          )}
        </div>
      </div>


      {/* Aucun élève dans la classe : toutes les gardes historiques testaient
          eleves_resultats en truthiness ([] est truthy) — la grille s'affichait
          sans lignes avec une synthèse « 0/0 » absurde. État vide explicite à
          la place (couvre aussi la saisie différée post-éval papier). */}
      {aucunEleve ? (
        <div className="flex flex-col items-center justify-center py-14 sm:py-20 bg-white border-2 border-[#D5E6F3] rounded-2xl shadow-sm">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#EAF4FB] rounded-full flex items-center justify-center mb-4 border border-[#D5E6F3]">
            <Users className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-600" />
          </div>
          <h3 className="text-lg sm:text-xl font-extrabold text-slate-800 mb-2">Aucun élève dans cette classe</h3>
          <p className="text-slate-500 text-center max-w-md text-sm sm:text-base px-4 font-semibold">
            Importez les élèves pour saisir des résultats.
            L'évaluation reste conservée : la grille de notation apparaîtra dès que la classe aura des élèves.
          </p>
        </div>
      ) : (
      <>
      {/* Légende-filtre : cliquer un niveau le met en évidence dans le
          tableau (les autres pastilles s'estompent). Remplace l'ancienne
          légende statique du bas de page. */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-xs font-extrabold text-slate-400">Maîtrise :</span>
        {NIVEAUX.map((n) => (
          <button
            key={n.v}
            onClick={() => setHighlightNiveau((prev) => (prev === n.v ? null : n.v))}
            className={`inline-flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-full border-2 transition-colors ${
              highlightNiveau === n.v ? 'border-slate-800 bg-[#F2F8FC]' : 'border-[#D5E6F3] bg-white hover:border-cyan-400'
            }`}
          >
            <span className="w-[11px] h-[11px] rounded-full" style={{ background: n.dot }} />
            {n.code}<span className="hidden lg:inline"> · {n.label}</span>
          </button>
        ))}
        <span className="ml-auto text-[11px] font-bold text-slate-400 hidden 2xl:block">
          Cliquez un niveau pour le mettre en évidence · cliquez une compétence pour trier
        </span>
      </div>

      {/* Tableau d'évaluation - Responsive */}
      <div ref={containerRef} className="overflow-auto max-h-[65vh] bg-white border-2 border-[#D5E6F3] rounded-2xl shadow-sm relative">
        {/* Render based on screen size */}
          {/* Pour l'instant on mets la vue mobile et tablette identique. */}
        {estEtroit ? renderCartesView() : renderDesktopView()}
      </div>
      </>
      )}

      <CompForEval
        eleveId={selectedEleveId}
        esfId={selectedEsfId} 
        show={modalShow} 
        handleClose={handleClose}
        setStoredResultatEval={setStoredResultatEval}
        onScoreChange={handleScoreChange}
      />

      {/* Notification de modifications non sauvegardées — bottom-20 sous xl :
          le burger flottant de la Sidebar occupe le coin bas-droit
          (fixed bottom-6 right-6 xl:hidden). */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-20 xl:bottom-2 right-2 sm:right-4 bg-blue-600 text-white p-2 rounded-lg shadow-lg z-50 max-w-[90vw] sm:max-w-sm">
          <div className="flex items-center text-xs sm:text-base 2xl:text-lg gap-2 mb-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <p className="font-medium">
              {pendingUpdates.size} modification(s) en attente
            </p>
          </div>
          {isLoadingPost ? (
            <Loading loadingText={"Mise à jour en cours..."} />
          ) : (
            <div className="flex gap-2">
              <button 
                onClick={handleBatchSave}
                disabled={isSaving}
                className="px-2 sm:px-3 py-1 sm:py-2 bg-green-500 hover:bg-green-600 rounded text-xs sm:text-sm font-medium disabled:opacity-50"
              >
                {isSaving ? "💾 Sauvegarde..." : "💾 Sauvegarder"}
              </button>
              
              <button
                onClick={handleCancelChanges}
                className="px-2 sm:px-3 py-1 sm:py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded text-xs sm:text-sm font-medium shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/50"
              >
                ❌ Annuler
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EvalDetail;
